import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import { TableHead } from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ReactTooltip from "react-tooltip";
import { useGetAssetsFinalQuery } from '../../../../features/addassets/addassetfinalApiSlice';
import { useSelector } from 'react-redux'
import useAuth from '../../../../features/hooks/useAuth'
import moment from 'moment';
import { useGetUsersQuery, useAddUserAssetsMutation } from '../../../../features/users/usersApiSlice';
import { TableContStyle, TableHeadStyle } from '../../../../Styles/TableStyles'

const SearchContainer = styled.div`
display: flex;
align-items: flex-end;
align-content: flex-end;
margin: 20px 5px;
`
const Span = styled.span`
width: fit-content;
align-self: center;
display: flex;
align-items: center;
justify-content: center;
font-size: 25px;
font-weight: 600;
align-self: center;
border: 0.5px solid black;
padding:  5px;
padding-left: 15px;
padding-right: 15px;
margin-top: 20px;
margin-bottom: 20px;
border-radius: 16px;
font-size: 24px;
background-color: #95def6;
-webkit-box-shadow: 4px 7px 9px 1px rgba(0,0,0,0.75);
-moz-box-shadow: 4px 7px 9px 1px rgba(0,0,0,0.75);
box-shadow: 4px 7px 9px 1px rgba(0,0,0,0.75);
`
const HeadingWrap = styled.div`
display: flex;
align-items: center;
justify-content: center;
`

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default function ReqCreatorOffAssetsTable({requestCreatorOff}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [selected, setSelected] = React.useState('name');
  const [query, setQuery] = React.useState('')

  //logged in user info
  const { id, email, designation, designationSuffix, numSuffix, office, roles } = useAuth()
  let user_current_office = (useSelector((state) => state.office.off))
  let user_current_role_array = roles[user_current_office]

  //Single user call
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  })

  //all assets call 
  const {
    data: allassets,
    isLoading,
    isSuccess,
    isError,
    error
  } = useGetAssetsFinalQuery('allassetlist', {
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  });

  let allassetdata = allassets ? Object.values(allassets?.entities) : []
  let allAssets = allassetdata || []

  //get single assets
  const { getSingleasset } = useGetAssetsFinalQuery('allassetlist', {
    selectFromResult: ({ data }) => ({
      getSingleasset: data?.entities
    }),
  })

  // filter assets with current user office.
  let officeAssets = allAssets.filter(elm =>  elm.alloc_ofc === requestCreatorOff)

  let rows = officeAssets||[]
  
  let filteredrows = rows?.filter(row => row[selected]?.toLowerCase()?.includes(query?.toLowerCase()))
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (<>
    <HeadingWrap>
      {rows?.length <1 && <Span>No assets in this office</Span>}
    </HeadingWrap>
    <TableContainer component={Paper} sx={{ backgroundColor: TableContStyle.backgroundColor }}>
      <Table aria-label="custom pagination table">
        <TableHead>
          <TableRow sx={{ backgroundColor: TableHeadStyle.backgroundColor }}>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }}>SLNo</TableCell>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} align="right">Asset</TableCell>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} align="right">OEM Tag</TableCell>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} align="right">OEM/Brand</TableCell>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} align="right">Currently with</TableCell>
            <TableCell style={{ fontSize: TableHeadStyle.fontSize, fontWeight: TableHeadStyle.fontWeight }} align="right">Warranty date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(query ? filteredrows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) :
            rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          ).map((row, index) => (
            <TableRow key={index}>
              <TableCell style={{ fontSize: 16, width: 60 }} component="th" scope="row">
                {index + 1}
                {row.in_transit && <div className="App">
                  <div data-tip data-for={row._id}>
                    **
                  </div>
                  <ReactTooltip id={row._id} place="top" effect="solid">
                    <div>
                      This assets is in transit with a request.
                    </div>
                  </ReactTooltip>
                </div>}
              </TableCell>
              <TableCell style={{ fontSize: 16, width: 160 }} align="right">
                <div className="App">
                  <div data-tip data-for={row._id}>
                    {row.name}
                  </div>
                  <ReactTooltip id={row._id} place="top" effect="solid">
                    <div>
                      {Object.entries(row.specification).map(([key, val]) => <h4 key={key}>{key}: {val}</h4>)}
                    </div>
                  </ReactTooltip>
                </div>
              </TableCell>
              <TableCell style={{ fontSize: 16, width: 160 }} align="right">
                {row.oem_tag}
              </TableCell>
              <TableCell style={{ fontSize: 16, width: 160 }} align="right">
                {row.oem}
              </TableCell>
              <TableCell style={{ fontSize: 16, width: 160 }} align="right">
                {row.cur_loc_name}
              </TableCell>
              <TableCell style={{ fontSize: 16, width: 250 }} align="right">
                {moment(row?.warrantyexp_date).format('DD/MM/YYYY')}
              </TableCell>
              {/* <TableCell style={{ width: 160 }} align="right">
                {row.Office}
              </TableCell>
              <TableCell style={{ width: 160 }} align="right">
                {row.IssueDate}
              </TableCell> */}
            </TableRow>
          ))}

          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={6} />
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: rows.length }]}
              colSpan={8}
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: {
                  'aria-label': 'rows per page',
                },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  </>
  );
}
